"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import maplibregl from "maplibre-gl";
import { Box } from "@mui/material";
import LayerCard from "./layerCard.component";
import LegendCard from "./legendCard.component";
import FieldImagesTooltip from "./fieldImagesTooltip.component";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  capitalizeFirst,
  getLatestMeasurementByName,
  getNdviStatus,
  getStatusLabel,
} from "../../../utils/digitalTwinUtils";
import { FileSource, Protocol, PMTiles } from "pmtiles";
import { fromArrayBuffer } from "geotiff";
import {
  setDTFarm,
  setDTField,
  setSinglePlant,
} from "../../../store/features/digitalTwin/selectedDigitalTwinSlice";
import { fetchFieldImageDetailStart } from "../../../store/features/digitalTwin/fieldImagesSlice";

const pmtilesProtocol = new Protocol();
let isPmtilesProtocolRegistered = false;
const PLANT_MARKER_SIZE = 40;
const PLANT_MARKER_BORDER_WIDTH = 1;
const PLANT_MARKER_REFERENCE_ZOOM = 17;
const PLANT_MARKER_ZOOM_SIZE_STEP = 6;
const PLANT_MARKER_MIN_SIZE = 5;
const PLANT_MARKER_MAX_SIZE = 35;
const PLANT_MARKER_HIDE_ZOOM = 15;
const PLANT_IMAGE_MATCH_MAX_DISTANCE_METERS = 6.5;
const IMAGE_MARKER_COLOR = "#1976d2";

export default function OpenStreetMap({
  selectedField,
  selectedFarm,
  fieldsData,
  farmsData,
  polygons,
  farmPolygons,
  plantsData,
  plantsMeasurementsData,
  downloadedRaster,
  rasterStatus,
  selectedRaster,
  setRasterStatus,
  latestSurvey,
  layers,
  setLayers,
  fieldImagesData,
  fieldImagesLoading,
  selectedFieldImage,
  selectedFieldImageId,
  fieldImageDetailLoading,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const imageMarkersRef = useRef([]);
  const [activeImagePopup, setActiveImagePopup] = useState(null);
  const fieldImages = useMemo(
    () => fieldImagesData?.data || [],
    [fieldImagesData],
  );

  const getRandomStatus = () =>
    ["ok", "fine", "bad"][Math.floor(Math.random() * 3)];

  const formatMeasurementValue = useCallback((value, unit = "") => {
    if (value === null || value === undefined || value === "") return "-";

    const numericValue = Number(value);
    const formattedValue = Number.isFinite(numericValue)
      ? new Intl.NumberFormat("it-IT", {
          maximumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
        }).format(numericValue)
      : value;

    return unit ? `${formattedValue} ${unit}` : formattedValue;
  }, []);

  const formatRelativeTime = useCallback((timestamp) => {
    if (!timestamp) return "-";

    const date = new Date(timestamp);
    const diffMs = date.getTime() - Date.now();
    const absDiffMs = Math.abs(diffMs);
    const rtf = new Intl.RelativeTimeFormat("it-IT", { numeric: "auto" });

    if (Number.isNaN(date.getTime())) return "-";
    if (absDiffMs < 60 * 1000)
      return rtf.format(Math.round(diffMs / 1000), "second");
    if (absDiffMs < 60 * 60 * 1000)
      return rtf.format(Math.round(diffMs / (60 * 1000)), "minute");
    if (absDiffMs < 24 * 60 * 60 * 1000)
      return rtf.format(Math.round(diffMs / (60 * 60 * 1000)), "hour");
    if (absDiffMs < 30 * 24 * 60 * 60 * 1000)
      return rtf.format(Math.round(diffMs / (24 * 60 * 60 * 1000)), "day");

    return date.toLocaleDateString("it-IT");
  }, []);

  const getPlantMeasurements = useCallback(
    (plantId) => {
      if (!Array.isArray(plantsMeasurementsData)) return [];

      return plantsMeasurementsData.filter(
        (measurement) => String(measurement?.plant_id) === String(plantId),
      );
    },
    [plantsMeasurementsData],
  );

  const getLatestPlantTimestamp = useCallback((measurements) => {
    return measurements
      .map((measurement) => measurement?.survey_timestamp)
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  }, []);

  const getPlantMarkerSize = useCallback((zoom) => {
    const size =
      PLANT_MARKER_SIZE +
      (zoom - PLANT_MARKER_REFERENCE_ZOOM) * PLANT_MARKER_ZOOM_SIZE_STEP;

    return Math.max(
      PLANT_MARKER_MIN_SIZE,
      Math.min(PLANT_MARKER_MAX_SIZE, Math.round(size)),
    );
  }, []);

  const applyPlantMarkerSize = useCallback(
    (element, zoom) => {
      const size = getPlantMarkerSize(zoom);

      element.style.width = `${size}px`;
      element.style.height = `${size}px`;
    },
    [getPlantMarkerSize],
  );

  const handlePlantDetailRedirect = useCallback(
    (plant) => {
      dispatch(setSinglePlant(plant));
      navigate(`/digitalTwin/${plant.id}`);
    },
    [dispatch, navigate],
  );

  const handleLayerChange = useCallback(
    (layerName) => {
      setLayers?.((currentLayers) => {
        const isActive = Boolean(currentLayers?.[layerName]);

        if (isActive) {
          return {
            ...currentLayers,
            [layerName]: false,
          };
        }

        return {
          ortofoto: layerName === "ortofoto",
          ndvi: layerName === "ndvi",
        };
      });
    },
    [setLayers],
  );

  const handlePolygonClick = useCallback(
    (event) => {
      const fieldIndex = Number(event.features?.[0]?.properties?.fieldIndex);
      const field = fieldsData?.[fieldIndex];

      if (field) {
        dispatch(setDTFarm(null));
        dispatch(setDTField(field));
      }
    },
    [dispatch, fieldsData],
  );

  const handleFarmPolygonClick = useCallback(
    (event) => {
      const farmIndex = Number(event.features?.[0]?.properties?.farmIndex);
      const farm = farmsData?.[farmIndex];

      if (farm) {
        dispatch(setDTField(null));
        dispatch(setDTFarm(farm));
      }
    },
    [dispatch, farmsData],
  );

  const handlePolygonMouseEnter = useCallback(() => {
    const map = mapRef.current;
    if (map) map.getCanvas().style.cursor = "pointer";
  }, []);

  const handlePolygonMouseLeave = useCallback(() => {
    const map = mapRef.current;
    if (map) map.getCanvas().style.cursor = "";
  }, []);

  const rasterStatusMessage = (() => {
    if (rasterStatus === "loading") return "Caricamento in corso...";
    if (rasterStatus === "applying") return "Applicazione layer in corso...";
    if (rasterStatus === "error") return "Layer non disponibile";
    return null;
  })();

  const toLngLat = useCallback(([first, second]) => {
    const firstCoordinate = Number(first);
    const secondCoordinate = Number(second);

    if (Number.isNaN(firstCoordinate) || Number.isNaN(secondCoordinate)) {
      return null;
    }

    // Field-data currently arrives as [lat, lng], while MapLibre expects [lng, lat].
    // Keep this tolerant in case an endpoint starts returning GeoJSON order.
    if (Math.abs(firstCoordinate) > 20 && Math.abs(secondCoordinate) <= 20) {
      return [secondCoordinate, firstCoordinate];
    }

    return [firstCoordinate, secondCoordinate];
  }, []);

  const getItemLngLat = useCallback(
    (item) => {
      const coordinateCandidate =
        item?.geom?.coordinates ||
        item?.geometry?.coordinates ||
        item?.coordinates ||
        item?.coordinate ||
        item?.position?.coordinates ||
        item?.position ||
        item?.gps_position ||
        item?.location?.coordinates ||
        item?.centroid?.coordinates ||
        item?.point?.coordinates;

      if (
        Array.isArray(coordinateCandidate) &&
        coordinateCandidate.length >= 2
      ) {
        return toLngLat(coordinateCandidate);
      }

      const lat =
        item?.lat ??
        item?.latitude ??
        item?.image_lat ??
        item?.image_latitude ??
        item?.gps_lat ??
        item?.gps_latitude;
      const lng =
        item?.lng ??
        item?.lon ??
        item?.long ??
        item?.longitude ??
        item?.image_lng ??
        item?.image_lon ??
        item?.image_long ??
        item?.image_longitude ??
        item?.gps_lng ??
        item?.gps_lon ??
        item?.gps_longitude;

      if (lat !== undefined && lng !== undefined) {
        return toLngLat([Number(lng), Number(lat)]);
      }

      return null;
    },
    [toLngLat],
  );

  const getDistanceMeters = useCallback(([lngA, latA], [lngB, latB]) => {
    const earthRadiusMeters = 6371000;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const deltaLat = toRadians(latB - latA);
    const deltaLng = toRadians(lngB - lngA);
    const haversine =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(toRadians(latA)) *
        Math.cos(toRadians(latB)) *
        Math.sin(deltaLng / 2) ** 2;

    return (
      2 *
      earthRadiusMeters *
      Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
    );
  }, []);

  const getPlantImages = useCallback(
    (plant) => {
      const directlyMatchedImages = fieldImages.filter((image) => {
        const imagePlantId = image?.plant_id || image?.plant;
        return imagePlantId && String(imagePlantId) === String(plant?.id);
      });

      if (directlyMatchedImages.length) {
        return directlyMatchedImages.sort((a, b) => {
          const timestampA = new Date(a?.timestamp || 0).getTime();
          const timestampB = new Date(b?.timestamp || 0).getTime();
          return timestampB - timestampA;
        });
      }

      const plantLngLat = getItemLngLat(plant);
      if (!plantLngLat || !Array.isArray(fieldImages)) return [];

      const plantDistancesByImage = fieldImages.map((image) => {
        const imageLngLat = getItemLngLat(image);
        if (!imageLngLat) return null;

        const nearestPlant = plantsData
          .map((candidatePlant) => {
            const candidateLngLat = getItemLngLat(candidatePlant);
            if (!candidateLngLat) return null;

            return {
              plant: candidatePlant,
              distance: getDistanceMeters(candidateLngLat, imageLngLat),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.distance - b.distance)[0];

        if (
          nearestPlant &&
          String(nearestPlant.plant?.id) !== String(plant?.id)
        ) {
          return null;
        }

        return {
          image,
          distance: getDistanceMeters(plantLngLat, imageLngLat),
        };
      });

      return plantDistancesByImage
        .filter(
          (match) =>
            match && match.distance <= PLANT_IMAGE_MATCH_MAX_DISTANCE_METERS,
        )
        .sort((a, b) => {
          const timestampA = new Date(a.image?.timestamp || 0).getTime();
          const timestampB = new Date(b.image?.timestamp || 0).getTime();
          return timestampB - timestampA;
        })
        .map((match) => match.image);
    },
    [fieldImages, getDistanceMeters, getItemLngLat, plantsData],
  );

  const getImagesByCoordinate = useCallback(() => {
    return fieldImages.reduce((groups, image) => {
      const imageLngLat = getItemLngLat(image);
      if (!imageLngLat) return groups;

      const [lng, lat] = imageLngLat;
      const key = `${lng.toFixed(7)}:${lat.toFixed(7)}`;

      if (!groups[key]) {
        groups[key] = {
          coordinates: imageLngLat,
          images: [],
        };
      }

      groups[key].images.push(image);
      groups[key].images.sort((a, b) => {
        const timestampA = new Date(a?.timestamp || 0).getTime();
        const timestampB = new Date(b?.timestamp || 0).getTime();
        return timestampB - timestampA;
      });

      return groups;
    }, {});
  }, [fieldImages, getItemLngLat]);

  const getPolygonLngLat = useCallback(
    (coords) => {
      const polygon = Array.isArray(coords?.[0]?.[0]) ? coords[0] : coords;
      return Array.isArray(polygon) ? polygon.map(toLngLat) : [];
    },
    [toLngLat],
  );

  const computeCenter = useCallback(
    (coords) => {
      const polygon = getPolygonLngLat(coords);
      const avgLng = polygon.reduce((s, [lng]) => s + lng, 0) / polygon.length;
      const avgLat =
        polygon.reduce((s, [, lat]) => s + lat, 0) / polygon.length;
      return { lat: avgLat, lng: avgLng };
    },
    [getPolygonLngLat],
  );

  const getBoundsFromCoords = useCallback((coords) => {
    if (!coords?.length) return null;

    return coords.reduce(
      (bounds, coord) => bounds.extend(coord),
      new maplibregl.LngLatBounds(coords[0], coords[0]),
    );
  }, []);

  const fitAllPolygons = useCallback(
    (map, options = {}) => {
      const allCoords = [
        ...polygons.flatMap(getPolygonLngLat),
        ...(farmPolygons || []).flatMap(getPolygonLngLat),
      ];
      const bounds = getBoundsFromCoords(allCoords);

      if (!bounds) return;

      map.fitBounds(bounds, {
        padding: 80,
        duration: 800,
        pitch: 0,
        bearing: 0,
        ...options,
      });
    },
    [farmPolygons, getBoundsFromCoords, getPolygonLngLat, polygons],
  );

  const utm32NToLngLat = useCallback((easting, northing) => {
    const a = 6378137;
    const eccSquared = 0.00669438;
    const k0 = 0.9996;
    const zoneNumber = 32;
    const x = easting - 500000;
    const y = northing;
    const longOrigin = (zoneNumber - 1) * 6 - 180 + 3;
    const eccPrimeSquared = eccSquared / (1 - eccSquared);
    const m = y / k0;
    const mu =
      m /
      (a *
        (1 -
          eccSquared / 4 -
          (3 * eccSquared ** 2) / 64 -
          (5 * eccSquared ** 3) / 256));
    const e1 =
      (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared));
    const phi1Rad =
      mu +
      ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
      ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
      ((151 * e1 ** 3) / 96) * Math.sin(6 * mu);
    const n1 = a / Math.sqrt(1 - eccSquared * Math.sin(phi1Rad) ** 2);
    const t1 = Math.tan(phi1Rad) ** 2;
    const c1 = eccPrimeSquared * Math.cos(phi1Rad) ** 2;
    const r1 =
      (a * (1 - eccSquared)) / (1 - eccSquared * Math.sin(phi1Rad) ** 2) ** 1.5;
    const d = x / (n1 * k0);
    const lat =
      phi1Rad -
      ((n1 * Math.tan(phi1Rad)) / r1) *
        (d ** 2 / 2 -
          ((5 + 3 * t1 + 10 * c1 - 4 * c1 ** 2 - 9 * eccPrimeSquared) *
            d ** 4) /
            24 +
          ((61 +
            90 * t1 +
            298 * c1 +
            45 * t1 ** 2 -
            252 * eccPrimeSquared -
            3 * c1 ** 2) *
            d ** 6) /
            720);
    const lng =
      longOrigin +
      ((d -
        ((1 + 2 * t1 + c1) * d ** 3) / 6 +
        ((5 -
          2 * c1 +
          28 * t1 -
          3 * c1 ** 2 +
          8 * eccPrimeSquared +
          24 * t1 ** 2) *
          d ** 5) /
          120) /
        Math.cos(phi1Rad)) *
        (180 / Math.PI);

    return [lng, lat * (180 / Math.PI)];
  }, []);

  const normalizeRasterBounds = useCallback(
    ([minX, minY, maxX, maxY]) => {
      if (
        Math.abs(minX) <= 180 &&
        Math.abs(maxX) <= 180 &&
        Math.abs(minY) <= 90 &&
        Math.abs(maxY) <= 90
      ) {
        return [minX, minY, maxX, maxY];
      }

      const sw = utm32NToLngLat(minX, minY);
      const ne = utm32NToLngLat(maxX, maxY);
      return [
        Math.min(sw[0], ne[0]),
        Math.min(sw[1], ne[1]),
        Math.max(sw[0], ne[0]),
        Math.max(sw[1], ne[1]),
      ];
    },
    [utm32NToLngLat],
  );

  const removeRasterLayers = useCallback((map) => {
    if (map.getLayer("orthomosaic-layer")) map.removeLayer("orthomosaic-layer");
    if (map.getSource("orthomosaic")) map.removeSource("orthomosaic");
    if (map.getLayer("geotiff-layer")) map.removeLayer("geotiff-layer");
    if (map.getSource("geotiff")) map.removeSource("geotiff");
  }, []);

  const createGeoTiffImage = useCallback(
    async (raster) => {
      const arrayBuffer =
        raster.arrayBuffer ||
        (raster.blob ? await raster.blob.arrayBuffer() : null) ||
        (raster.url
          ? await fetch(raster.url).then((response) => {
              if (!response.ok) {
                throw new Error(`Errore download GeoTIFF: ${response.status}`);
              }
              return response.arrayBuffer();
            })
          : null);

      if (!arrayBuffer) {
        throw new Error("GeoTIFF non disponibile");
      }

      const tiff = await fromArrayBuffer(arrayBuffer);
      const image = await tiff.getImage();
      const sourceWidth = image.getWidth();
      const sourceHeight = image.getHeight();
      const maxSize = 2048;
      const scale = Math.min(1, maxSize / sourceWidth, maxSize / sourceHeight);
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const sampleCount = image.getSamplesPerPixel();
      const samples =
        sampleCount >= 4 ? [0, 1, 2, 3] : sampleCount >= 3 ? [0, 1, 2] : [0];
      const rasters = await image.readRasters({
        samples,
        width,
        height,
        interleave: false,
        resampleMethod: "nearest",
      });
      const noData = image.getGDALNoData();
      const output = new Uint8ClampedArray(width * height * 4);

      const getMinMax = (band) => {
        let min = Infinity;
        let max = -Infinity;

        for (let i = 0; i < band.length; i += 1) {
          const value = band[i];
          if (value === noData || !Number.isFinite(value)) continue;
          if (value < min) min = value;
          if (value > max) max = value;
        }

        return min === Infinity || max === -Infinity || min === max
          ? [0, 255]
          : [min, max];
      };

      const minMaxByBand = rasters.map(getMinMax);
      const normalize = (value, bandIndex) => {
        if (value === noData || !Number.isFinite(value)) return 0;
        const [min, max] = minMaxByBand[bandIndex];
        return Math.max(0, Math.min(255, ((value - min) / (max - min)) * 255));
      };

      for (let i = 0; i < width * height; i += 1) {
        const outputIndex = i * 4;

        if (samples.length >= 3) {
          output[outputIndex] = normalize(rasters[0][i], 0);
          output[outputIndex + 1] = normalize(rasters[1][i], 1);
          output[outputIndex + 2] = normalize(rasters[2][i], 2);
        } else {
          const value = normalize(rasters[0][i], 0);
          output[outputIndex] = value;
          output[outputIndex + 1] = value;
          output[outputIndex + 2] = value;
        }

        if (samples.length >= 4) {
          output[outputIndex + 3] =
            rasters[3][i] === 0 ? 0 : normalize(rasters[3][i], 3);
        } else {
          output[outputIndex + 3] = rasters[0][i] === noData ? 0 : 255;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas
        .getContext("2d")
        .putImageData(new ImageData(output, width, height), 0, 0);

      const [minLon, minLat, maxLon, maxLat] = normalizeRasterBounds(
        image.getBoundingBox(),
      );

      return {
        canvas,
        coordinates: [
          [minLon, maxLat],
          [maxLon, maxLat],
          [maxLon, minLat],
          [minLon, minLat],
        ],
        bounds: [minLon, minLat, maxLon, maxLat],
      };
    },
    [normalizeRasterBounds],
  );

  const createDerivedNdviImage = useCallback(
    async ({ redArrayBuffer, nirArrayBuffer }) => {
      if (!redArrayBuffer || !nirArrayBuffer) {
        throw new Error("Bande Red/NIR non disponibili");
      }

      const [redTiff, nirTiff] = await Promise.all([
        fromArrayBuffer(redArrayBuffer),
        fromArrayBuffer(nirArrayBuffer),
      ]);
      const [redImage, nirImage] = await Promise.all([
        redTiff.getImage(),
        nirTiff.getImage(),
      ]);

      const sourceWidth = Math.min(redImage.getWidth(), nirImage.getWidth());
      const sourceHeight = Math.min(redImage.getHeight(), nirImage.getHeight());
      const maxSize = 2048;
      const scale = Math.min(1, maxSize / sourceWidth, maxSize / sourceHeight);
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const [redBands, nirBands] = await Promise.all([
        redImage.readRasters({ samples: [0], width, height, interleave: false }),
        nirImage.readRasters({ samples: [0], width, height, interleave: false }),
      ]);
      const red = redBands[0];
      const nir = nirBands[0];
      const redNoData = redImage.getGDALNoData();
      const nirNoData = nirImage.getGDALNoData();
      const output = new Uint8ClampedArray(width * height * 4);

      const getNdviColor = (value) => {
        const normalized = Math.max(0, Math.min(1, (value + 1) / 2));
        if (normalized < 0.25) return [165, 0, 38];
        if (normalized < 0.4) return [244, 109, 67];
        if (normalized < 0.55) return [254, 224, 139];
        if (normalized < 0.7) return [166, 217, 106];
        return [0, 104, 55];
      };

      for (let i = 0; i < width * height; i += 1) {
        const redValue = red[i];
        const nirValue = nir[i];
        const outputIndex = i * 4;
        const isNoData =
          !Number.isFinite(redValue) ||
          !Number.isFinite(nirValue) ||
          redValue === redNoData ||
          nirValue === nirNoData ||
          redValue + nirValue === 0;

        if (isNoData) {
          output[outputIndex + 3] = 0;
          continue;
        }

        const [r, g, b] = getNdviColor((nirValue - redValue) / (nirValue + redValue));
        output[outputIndex] = r;
        output[outputIndex + 1] = g;
        output[outputIndex + 2] = b;
        output[outputIndex + 3] = 220;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").putImageData(new ImageData(output, width, height), 0, 0);
      const [minLon, minLat, maxLon, maxLat] = normalizeRasterBounds(
        nirImage.getBoundingBox(),
      );

      return {
        canvas,
        coordinates: [
          [minLon, maxLat],
          [maxLon, maxLat],
          [maxLon, minLat],
          [minLon, minLat],
        ],
        bounds: [minLon, minLat, maxLon, maxLat],
      };
    },
    [normalizeRasterBounds],
  );

  // -------------------- Init Map + PMTiles --------------------

  useEffect(() => {
    if (!mapContainer.current) return;

    // Register protocol if not already present
    if (!isPmtilesProtocolRegistered) {
      try {
        maplibregl.addProtocol("pmtiles", (params, abortController) =>
          pmtilesProtocol.tile(params, abortController),
        );
        isPmtilesProtocolRegistered = true;
      } catch (e) {
        isPmtilesProtocolRegistered = true;
      }
    }

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: [
              "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            ],
            tileSize: 256,
            attribution:
              "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
            maxzoom: 19,
          },
        },
        layers: [
          {
            id: "osm-layer",
            type: "raster",
            source: "osm",
          },
        ],
      },
      center: [8.4, 40.5],
      zoom: 15,
      pitch: 0,
      bearing: 0,
      fitBoundsOptions: { padding: 0 },
    });

    map.addControl(
      new maplibregl.NavigationControl({
        showCompass: true,
        showZoom: true,
        visualizePitch: true,
      }),
      "bottom-left",
    );

    mapRef.current = map;

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // -------------------- Add raster when downloaded --------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let isCurrent = true;

    const hasRasterPayload =
      Boolean(downloadedRaster?.url) ||
      Boolean(downloadedRaster?.blob) ||
      (downloadedRaster?.rasterKind === "ndvi-derived" &&
        Boolean(downloadedRaster?.redArrayBuffer) &&
        Boolean(downloadedRaster?.nirArrayBuffer));

    if (!hasRasterPayload) {
      removeRasterLayers(map);
      return;
    }

    const addRaster = async () => {
      try {
        removeRasterLayers(map);

        if (
          downloadedRaster.rasterKind === "geotiff" ||
          downloadedRaster.rasterKind === "ndvi-derived"
        ) {
          console.info("Applicazione GeoTIFF/COG sulla mappa:", {
            url: downloadedRaster.url,
            sourceKey: downloadedRaster.sourceKey,
            hasBlob: Boolean(downloadedRaster.blob),
            hasArrayBuffer: Boolean(downloadedRaster.arrayBuffer),
          });

          const geoTiffImage = downloadedRaster.rasterKind === "ndvi-derived"
            ? await createDerivedNdviImage(downloadedRaster)
            : await createGeoTiffImage(downloadedRaster);
          if (!isCurrent) return;
          console.log("GeoTIFF bounds:", geoTiffImage.bounds);

          map.addSource("geotiff", {
            type: "image",
            url: geoTiffImage.canvas.toDataURL("image/png"),
            coordinates: geoTiffImage.coordinates,
          });

          map.addLayer(
            {
              id: "geotiff-layer",
              type: "raster",
              source: "geotiff",
              layout: {
                visibility: "visible",
              },
              paint: {
                "raster-opacity": 1,
                "raster-contrast": 0.45,
                "raster-saturation": 0.5,
                "raster-brightness-min": 0.05,
                "raster-brightness-max": 0.85,
              },
            },
            map.getLayer("poly-fill") ? "poly-fill" : undefined,
          );
          map.moveLayer(
            "geotiff-layer",
            map.getLayer("poly-fill") ? "poly-fill" : undefined,
          );
          map.triggerRepaint();
          setRasterStatus?.("ready");
          return;
        }

        const sourceKey = downloadedRaster.sourceKey || downloadedRaster.url;
        const pmtiles = downloadedRaster.blob
          ? new PMTiles(
              new FileSource(
                new File([downloadedRaster.blob], sourceKey, {
                  type:
                    downloadedRaster.blob.type || "application/octet-stream",
                }),
              ),
            )
          : new PMTiles(downloadedRaster.url);

        pmtilesProtocol.add(pmtiles);
        const header = await pmtiles.getHeader();
        if (!isCurrent) return;
        console.log("PMTiles header bounds:", {
          minLon: header.minLon,
          minLat: header.minLat,
          maxLon: header.maxLon,
          maxLat: header.maxLat,
          minZoom: header.minZoom,
          maxZoom: header.maxZoom,
        });

        map.addSource("orthomosaic", {
          type: "raster",
          url: `pmtiles://${sourceKey}`,
          tileSize: header.tileSize || 512,
          attribution: "UNISS",
          minzoom: header.minZoom,
          maxzoom: header.maxZoom,
        });

        map.addLayer(
          {
            id: "orthomosaic-layer",
            type: "raster",
            source: "orthomosaic",
            layout: {
              visibility: "visible",
            },
            paint: {
              "raster-opacity": 1,
              "raster-contrast": 0.35,
              "raster-saturation": 0.4,
              "raster-brightness-min": 0.05,
              "raster-brightness-max": 0.9,
            },
          },
          map.getLayer("poly-fill") ? "poly-fill" : undefined,
        );
        map.moveLayer(
          "orthomosaic-layer",
          map.getLayer("poly-fill") ? "poly-fill" : undefined,
        );
        map.triggerRepaint();
        setRasterStatus?.("ready");

        if (selectedField?.geom?.coordinates?.[0]?.length) {
          const selectedFieldCoords = getPolygonLngLat(
            selectedField.geom.coordinates,
          );
          const selectedFieldBounds = getBoundsFromCoords(selectedFieldCoords);
          if (!selectedFieldBounds) return;

          const rasterIntersectsSelectedField =
            header.minLon <= selectedFieldBounds.getEast() &&
            header.maxLon >= selectedFieldBounds.getWest() &&
            header.minLat <= selectedFieldBounds.getNorth() &&
            header.maxLat >= selectedFieldBounds.getSouth();

          if (!rasterIntersectsSelectedField) {
            console.warn(
              "Il PMTiles e il selectedField non si sovrappongono.",
              {
                pmtilesBounds: [
                  header.minLon,
                  header.minLat,
                  header.maxLon,
                  header.maxLat,
                ],
                selectedFieldBounds: [
                  selectedFieldBounds.getWest(),
                  selectedFieldBounds.getSouth(),
                  selectedFieldBounds.getEast(),
                  selectedFieldBounds.getNorth(),
                ],
              },
            );
          }
          map.fitBounds(selectedFieldBounds, {
            padding: 80,
            duration: 800,
            pitch: 0,
            bearing: 0,
          });
          return;
        }
      } catch (err) {
        console.error("Error applying raster:", err);
        if (isCurrent) setRasterStatus?.("error");
      }
    };

    if (map.isStyleLoaded()) {
      addRaster();
    } else {
      map.once("load", addRaster);
    }

    return () => {
      isCurrent = false;
    };
  }, [
    createDerivedNdviImage,
    createGeoTiffImage,
    downloadedRaster,
    getBoundsFromCoords,
    getPolygonLngLat,
    removeRasterLayers,
    selectedField,
    setRasterStatus,
  ]);

  // -------------------- Draw Polygons --------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !polygons?.length) return;

    const addPolygons = () => {
      if (mapRef.current !== map) return;

      if (map.getLayer("poly-fill")) {
        map.off("click", "poly-fill", handlePolygonClick);
        map.off("mouseenter", "poly-fill", handlePolygonMouseEnter);
        map.off("mouseleave", "poly-fill", handlePolygonMouseLeave);
      }
      if (map.getLayer("poly-fill")) map.removeLayer("poly-fill");
      if (map.getLayer("poly-stroke")) map.removeLayer("poly-stroke");
      if (map.getSource("polygons")) map.removeSource("polygons");

      map.addSource("polygons", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: polygons.map((coords, fieldIndex) => ({
            type: "Feature",
            properties: { fieldIndex },
            geometry: {
              type: "Polygon",
              coordinates: [getPolygonLngLat(coords)],
            },
          })),
        },
      });

      map.addLayer({
        id: "poly-fill",
        type: "fill",
        source: "polygons",
        paint: {
          "fill-color": "clear", //theme.palette.primary.main,
          "fill-opacity": 0.2,
        },
      });

      map.on("click", "poly-fill", handlePolygonClick);
      map.on("mouseenter", "poly-fill", handlePolygonMouseEnter);
      map.on("mouseleave", "poly-fill", handlePolygonMouseLeave);

      map.addLayer({
        id: "poly-stroke",
        type: "line",
        source: "polygons",
        paint: { "line-color": "white", "line-width": 2 },
        /* theme.palette.primary.dark */
      });

      if (!selectedField && !selectedFarm) {
        fitAllPolygons(map, { duration: 0 });
      }
      map.triggerRepaint();
    };

    if (map.isStyleLoaded()) addPolygons();
    else map.once("load", addPolygons);

    return () => {
      try {
        map.off("load", addPolygons);

        if (mapRef.current === map && map.getLayer("poly-fill")) {
          map.off("click", "poly-fill", handlePolygonClick);
          map.off("mouseenter", "poly-fill", handlePolygonMouseEnter);
          map.off("mouseleave", "poly-fill", handlePolygonMouseLeave);
        }
      } catch (error) {
        // MapLibre can already have disposed the style while React is unmounting.
      }
    };
  }, [
    fitAllPolygons,
    getPolygonLngLat,
    handlePolygonClick,
    handlePolygonMouseEnter,
    handlePolygonMouseLeave,
    polygons,
    selectedFarm,
    selectedField,
  ]);

  // -------------------- Draw Farm Polygons --------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const addFarmPolygons = () => {
      if (mapRef.current !== map) return;

      if (map.getLayer("farm-fill")) {
        map.off("click", "farm-fill", handleFarmPolygonClick);
        map.off("mouseenter", "farm-fill", handlePolygonMouseEnter);
        map.off("mouseleave", "farm-fill", handlePolygonMouseLeave);
      }
      if (map.getLayer("farm-fill")) map.removeLayer("farm-fill");
      if (map.getLayer("farm-stroke")) map.removeLayer("farm-stroke");
      if (map.getSource("farm-polygons")) map.removeSource("farm-polygons");

      if (!farmPolygons?.length) return;

      map.addSource("farm-polygons", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: farmPolygons
            .map((coords, farmIndex) => ({
              type: "Feature",
              properties: { farmIndex },
              geometry: {
                type: "Polygon",
                coordinates: [getPolygonLngLat(coords)],
              },
            }))
            .filter((feature) => feature.geometry.coordinates[0].length > 0),
        },
      });

      map.addLayer({
        id: "farm-fill",
        type: "fill",
        source: "farm-polygons",
        paint: {
          "fill-color": "#8d6e63",
          "fill-opacity": 0.12,
        },
      });

      map.on("click", "farm-fill", handleFarmPolygonClick);
      map.on("mouseenter", "farm-fill", handlePolygonMouseEnter);
      map.on("mouseleave", "farm-fill", handlePolygonMouseLeave);

      map.addLayer({
        id: "farm-stroke",
        type: "line",
        source: "farm-polygons",
        paint: { "line-color": "#8d6e63", "line-width": 2 },
      });

      if (!selectedField && !selectedFarm) {
        fitAllPolygons(map, { duration: 0 });
      }
      map.triggerRepaint();
    };

    if (map.isStyleLoaded()) addFarmPolygons();
    else map.once("load", addFarmPolygons);

    return () => {
      try {
        map.off("load", addFarmPolygons);

        if (mapRef.current === map && map.getLayer("farm-fill")) {
          map.off("click", "farm-fill", handleFarmPolygonClick);
          map.off("mouseenter", "farm-fill", handlePolygonMouseEnter);
          map.off("mouseleave", "farm-fill", handlePolygonMouseLeave);
        }
      } catch (error) {
        // MapLibre can already have disposed the style while React is unmounting.
      }
    };
  }, [
    farmPolygons,
    fitAllPolygons,
    getPolygonLngLat,
    handleFarmPolygonClick,
    handlePolygonMouseEnter,
    handlePolygonMouseLeave,
    selectedFarm,
    selectedField,
  ]);

  // -------------------- reset markers --------------------
  useEffect(() => {
    // Se selectedField è null (o undefined/falsy), svuota i marker
    if (!selectedField) {
      if (markersRef.current) {
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];
      }
      if (imageMarkersRef.current) {
        imageMarkersRef.current.forEach((m) => m.remove());
        imageMarkersRef.current = [];
      }
      setActiveImagePopup(null);
    }
  }, [selectedField]);

  useEffect(() => {
    setActiveImagePopup(null);
  }, [selectedField?.id]);

  // -------------------- Plant markers --------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (!selectedField || !plantsData?.length) return;

    plantsData.forEach((plant) => {
      const [lng, lat] = toLngLat(plant.geom.coordinates);
      const plantMeasurements = getPlantMeasurements(plant.id);
      const ndvi = getLatestMeasurementByName(plantMeasurements, "ndvi");
      const height = getLatestMeasurementByName(plantMeasurements, "height");
      const volume = getLatestMeasurementByName(plantMeasurements, "volume");
      const area = getLatestMeasurementByName(plantMeasurements, "area");
      const count = getLatestMeasurementByName(plantMeasurements, "count");
      const status = ndvi
        ? getNdviStatus(ndvi.measurement_value)
        : getRandomStatus();
      const latestTimestamp = getLatestPlantTimestamp(plantMeasurements);
      const plantImages = getPlantImages(plant);

      const color =
        status === "fine"
          ? "#2ecc71"
          : status === "bad"
            ? "#ff0000"
            : "#f1c40f";

      /* this is the marker as square - test */
      /*     const el = document.createElement("div");
      applyPlantMarkerSize(el, map.getZoom());
      el.style.boxSizing = "border-box";
      el.style.border = `${PLANT_MARKER_BORDER_WIDTH}px solid ${color}`;
      el.style.borderRadius = "0";
      el.style.backgroundColor = "transparent";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.style.boxShadow = "none"; */

      const el = document.createElement("div");

      applyPlantMarkerSize(el, map.getZoom());

      el.style.boxSizing = "border-box";
      el.style.border = `${PLANT_MARKER_BORDER_WIDTH}px solid ${color}`;
      el.style.borderRadius = "50%";
      el.style.backgroundColor = color;
      el.style.overflow = "hidden";

      const img = document.createElement("img");
      img.src = "/icons/plant.png";
      img.style.width = "80%";
      img.style.height = "80%";
      img.style.objectFit = "cover";

      el.appendChild(img);

      const popupContent = document.createElement("div");
      popupContent.style.padding = "0";
      popupContent.style.border = "0";
      popupContent.style.background = "transparent";
      popupContent.style.textAlign = "left";
      popupContent.style.font = "inherit";
      popupContent.setAttribute(
        "aria-label",
        `Apri dettaglio ${plant.plant_type} ${plant.plant_number}`,
      );
      popupContent.innerHTML = `
        <div style="font-family: Arial, sans-serif; min-width: 220px; max-width: 320px; padding: 8px 10px 6px;">
          <div data-plant-detail style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; cursor: pointer;">
            <span style="display: inline-flex; align-items: center; min-height: 38px; padding: 4px 14px; border: 2px solid ${color}; border-radius: 14px; color: ${color}; background: ${color}18; font-size: 16px; line-height: 1; font-weight: 500;">
              ${getStatusLabel(status)}
            </span>
            <span style="color: #777; font-size: 18px; line-height: 1;">&#8250;</span>
          </div>
          <h3 data-plant-detail style="margin: 0; color: #000; font-size: 16px; line-height: 1.15; font-weight: 800; cursor: pointer;">
            ${capitalizeFirst(plant.plant_type)} #${String(plant.plant_number ?? plant.id).padStart(3, "0")}
          </h3>
          <p style="margin: 8px 0 20px; color: #aaa; font-size: 14px; line-height: 1.2;">
            Ultima rilevazione: ${formatRelativeTime(latestTimestamp)}
          </p>
          <div style="margin: 0 0 16px; color: #777; font-size: 14px; line-height: 1.2;">
            Immagini associate: <strong style="color: #222;">${plantImages.length}</strong>
          </div>
          <div style="display: grid; grid-template-columns: 0.7fr auto; row-gap: 10px; column-gap: 20px; color: #777; font-size: 16px; line-height: 1.1;">
            <span>Altezza</span><strong style="color: #222; font-weight: 500;">${formatMeasurementValue(height?.measurement_value, "cm")}</strong>
            <span>Volume</span><strong style="color: #222; font-weight: 500;">${formatMeasurementValue(volume?.measurement_value, "m³")}</strong>
            <span>Sup. occ.</span><strong style="color: #222; font-weight: 500;">${formatMeasurementValue(area?.measurement_value, "m²")}</strong>
            <span>N. capolini</span><strong style="color: #222; font-weight: 500;">${formatMeasurementValue(count?.measurement_value)}</strong>
          </div>
        </div>`;
      popupContent.addEventListener("click", (event) => {
        if (event.target.closest("[data-plant-detail]")) {
          handlePlantDetailRedirect(plant);
        }
      });

      el.addEventListener("click", () => {
        if (!plantImages.length) return;

        if (plantImages[0]?.id) {
          dispatch(fetchFieldImageDetailStart({ image_id: plantImages[0].id }));
        }
      });

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .setPopup(
          new maplibregl.Popup({ offset: 25 }).setDOMContent(popupContent),
        )
        .addTo(map);

      markersRef.current.push(marker);
    });
  }, [
    applyPlantMarkerSize,
    dispatch,
    formatMeasurementValue,
    formatRelativeTime,
    getLatestPlantTimestamp,
    getPlantImages,
    getPlantMeasurements,
    handlePlantDetailRedirect,
    plantsData,
    selectedField,
    toLngLat,
  ]);

  // -------------------- Image markers --------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    imageMarkersRef.current.forEach((m) => m.remove());
    imageMarkersRef.current = [];
    setActiveImagePopup(null);

    if (!selectedField || !fieldImages.length) return;

    Object.values(getImagesByCoordinate()).forEach(
      ({ coordinates, images }) => {
        if (!coordinates || !images.length) return;

        const el = document.createElement("button");
        el.type = "button";
        el.title = `${images.length} immagini`;
        el.style.width = "28px";
        el.style.height = "28px";
        el.style.borderRadius = "50%";
        el.style.border = `2px solid ${IMAGE_MARKER_COLOR}`;
        el.style.background = "#fff";
        el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
        el.style.cursor = "pointer";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.padding = "0";
        el.style.color = IMAGE_MARKER_COLOR;
        el.style.fontSize = "11px";
        el.style.fontWeight = "800";
        el.style.lineHeight = "1";
        el.textContent = images.length > 9 ? "9+" : String(images.length);

        const popupContent = document.createElement("div");
        popupContent.style.minWidth = "250px";
        popupContent.style.maxWidth = "270px";

        const popup = new maplibregl.Popup({
          offset: 18,
          maxWidth: "290px",
          closeButton: true,
          closeOnClick: true,
          className: "custom-field-images-popup",
        }).setDOMContent(popupContent);

        popup.on("open", () => {
          setActiveImagePopup({
            container: popupContent,
            coordinates,
            images,
          });
          if (images[0]?.id) {
            dispatch(fetchFieldImageDetailStart({ image_id: images[0].id }));
          }
        });

        popup.on("close", () => {
          setActiveImagePopup((current) =>
            current?.container === popupContent ? null : current,
          );
        });

        el.addEventListener("click", () => {
          if (images[0]?.id) {
            dispatch(fetchFieldImageDetailStart({ image_id: images[0].id }));
          }
        });

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat(coordinates)
          .setPopup(popup)
          .addTo(map);

        imageMarkersRef.current.push(marker);
      },
    );
  }, [dispatch, fieldImages.length, getImagesByCoordinate, selectedField]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const updateMarkerSizes = () => {
      markersRef.current.forEach((marker) => {
        applyPlantMarkerSize(marker.getElement(), map.getZoom());
      });
    };

    updateMarkerSizes();
    map.on("zoom", updateMarkerSizes);

    return () => {
      map.off("zoom", updateMarkerSizes);
    };
  }, [applyPlantMarkerSize, plantsData, selectedField]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedField) return;

    const resetFieldOnZoomOut = () => {
      if (map.getZoom() < PLANT_MARKER_HIDE_ZOOM) {
        dispatch(setDTField(null));
      }
    };

    map.on("zoomend", resetFieldOnZoomOut);

    return () => {
      map.off("zoomend", resetFieldOnZoomOut);
    };
  }, [dispatch, selectedField]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || selectedField || selectedFarm || !fieldsData?.length) return;

    const selectFieldOnZoomIn = () => {
      if (
        map.getZoom() < PLANT_MARKER_HIDE_ZOOM ||
        !map.getLayer("poly-fill")
      ) {
        return;
      }

      const centerPoint = map.project(map.getCenter());
      const features = map.queryRenderedFeatures(centerPoint, {
        layers: ["poly-fill"],
      });
      const fieldIndex = Number(features?.[0]?.properties?.fieldIndex);
      const field = fieldsData?.[fieldIndex];

      if (field) {
        dispatch(setDTFarm(null));
        dispatch(setDTField(field));
      }
    };

    map.on("zoomend", selectFieldOnZoomIn);

    return () => {
      map.off("zoomend", selectFieldOnZoomIn);
    };
  }, [dispatch, fieldsData, selectedFarm, selectedField]);

  // -------------------- Focus on selected field/farm --------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!selectedField && !selectedFarm) {
      fitAllPolygons(map);
      return;
    }

    const selectedArea = selectedField || selectedFarm;
    if (!selectedArea?.geom?.coordinates) return;

    const center = computeCenter(selectedArea.geom.coordinates);
    map.easeTo({ zoom: 17, pitch: 0, bearing: 0, center, duration: 800 });
  }, [computeCenter, fitAllPolygons, selectedFarm, selectedField]);

  // -------------------- Render --------------------
  return (
    <div style={{ position: "relative", width: "100%", height: "100vh" }}>
      <div
        ref={mapContainer}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
      {rasterStatusMessage && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 100, sm: 100 },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 12,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            backgroundColor: "background.paper",
            color: "text.secondary",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {rasterStatusMessage}
        </Box>
      )}
      {selectedField && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 64, sm: 80 },
            right: { xs: 6, sm: 10 },
            zIndex: 10,
            transform: { xs: "scale(0.82)", sm: "none" },
            transformOrigin: "top right",
          }}
        >
          <LayerCard layers={layers} onLayerChange={handleLayerChange} />
        </Box>
      )}

      {selectedField && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 230, sm: 300 },
            right: { xs: 6, sm: 10 },
            zIndex: 10,
            transform: { xs: "scale(0.82)", sm: "none" },
            transformOrigin: "top right",
          }}
        >
          <LegendCard />
        </Box>
      )}

      {activeImagePopup?.container &&
        createPortal(
          <FieldImagesTooltip
            coordinates={activeImagePopup.coordinates}
            images={activeImagePopup.images}
            selectedImage={selectedFieldImage}
            selectedImageId={selectedFieldImageId}
            isLoading={fieldImagesLoading}
            isDetailLoading={fieldImageDetailLoading}
          />,
          activeImagePopup.container,
        )}

      <style>{`
        .custom-field-images-popup .maplibregl-popup-content {
          padding: 10px 12px 12px 12px;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
          border: 1px solid rgba(0, 0, 0, 0.08);
        }
        .custom-field-images-popup .maplibregl-popup-close-button {
          padding: 6px 10px;
          font-size: 18px;
          color: #64748b;
          border-radius: 0 12px 0 6px;
        }
        .custom-field-images-popup .maplibregl-popup-close-button:hover {
          background-color: #f1f5f9;
          color: #0f172a;
        }
      `}</style>
    </div>
  );
}
